import { TemplateSettings } from "api/landlord/templates";
import { RuleSet } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { ScreeningCriteriaType } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import {
  ApplicantType,
  VerificationNames as VerificationNamesE,
  DataVendors,
  ExecutionModes
} from "shared/types";

type Properties = {
  title?: string;
  description: string;
  type: string;
  groupKey?: string;
  const?: string;
  enum?: string[];
  anyOf?: {
    enum: string[];
    title: string;
    type: string;
  }[];
};

type VerificationPropertiesBase = {
  [key: string]: Properties & {
    properties?: VerificationProperties;
    items?: {
      properties?: VerificationProperties;
      allOf?: VerificationAllOfProperties[];
      dependencies?: {
        [key: string]: {
          oneOf: {
            properties: Properties;
            required?: string[];
          }[];
        };
      };
    };
  };
};

export type VerificationAllOfProperties = {
  if: {
    properties?: VerificationProperties;
  };
  then: {
    properties?: VerificationProperties;
  };
};

export type VerificationProperties = VerificationPropertiesBase & {
  properties?: VerificationProperties;
};

export type JsonSchema = {
  title: string;
  type: string;
  required?: string[];
  properties?: VerificationProperties;
  allOf?: VerificationAllOfProperties[];
};

export enum DocumentTypeVerification {
  BankStatements = "bank_statements",
  PayStubs = "pay_stubs",
  W2 = "w2",
  TaxReturns = "tax_returns"
}

export type DocumentTimeframe = {
  [key in DocumentTypeVerification]?: { value: number; unit: "month" | "year" };
};

export type VerifiedIncome = {
  allow_bank?: boolean;
  allow_payroll?: boolean;
  allow_document?: boolean;
  document_timeframe?: DocumentTimeframe;
};

export type VerificationStep = {
  name: string;
  json_schema: JsonSchema;
};

export type VerificationSettings = {
  additional_questions?: Question[];
  additional_text?: string;
  allow_skip_verification?: boolean;
  hide_report?: boolean;
  // Only for verified income - TODO adjust types
  allow_bank?: boolean;
  allow_payroll?: boolean;
  allow_document?: boolean;
  document_timeframe?: DocumentTimeframe;
  vendor?: DataVendors;
  execution_mode?: ExecutionModes;
  selfie_check?: boolean;
  exclude_medical_debt?: boolean;
  exclude_student_debt?: boolean;
};

type HousingHistorySettings =
  | {
      address_step_enabled?: boolean;
      rent_option_required?: {
        landlord_phone: boolean;
        monthly_payment: boolean;
        reason_for_leaving: boolean;
        lease_document: boolean;
      };
      own_home_option_required?: {
        reason_for_leaving: boolean;
        monthly_mortgage_payment: boolean;
      };
      other_option_required?: {
        housing_status: boolean;
        reason_for_leaving: boolean;
      };
    }
  | undefined;

type VerificationNames = Partial<{
  [VerificationNamesE.CreditReport]: VerificationSettings;
  [VerificationNamesE.CriminalReport]: VerificationSettings;
  [VerificationNamesE.EvictionReport]: VerificationSettings;
  [VerificationNamesE.IdentityVerification]: VerificationSettings;
  [VerificationNamesE.IncomeVerification]: VerificationSettings & {
    income_verification_step_enabled?: boolean;
  };
  [VerificationNamesE.RentalApplication]: VerificationSettings;
  [VerificationNamesE.RentalVerification]: VerificationSettings;
  [VerificationNamesE.VerifiedIncomeVerification]: VerificationSettings;
  [VerificationNamesE.TermsAndConditions]: VerificationSettings;
  [VerificationNamesE.SubmissionDisclosure]: VerificationSettings;
  [VerificationNamesE.HousingHistory]: HousingHistorySettings;
}>;

export type VerificationSchemaNames = VerificationNames;

export type VerificationSchemas = {
  [key in ApplicantType]: VerificationNames;
};

export type VerificationSchema = {
  id: string;
  name: VerificationNamesE;
  title: string;
  version: number;
  description: string;
  editable: boolean;
  estimated_time: number;
  settings?: VerificationSettings;
  steps: VerificationStep[];
  visible: boolean;
  screening_fee: ApplicationFee;
  coming_soon?: boolean;
  required: boolean;
};

export type IdentityVerificationSettings = {
  applicant: {
    identity_verification?: {
      selfie_check: boolean;
      prevent_apply_if_failed?: boolean;
    };
    enabled_screening_services?: boolean;
  };
  co_signer: {
    identity_verification?: {
      selfie_check: boolean;
      prevent_apply_if_failed?: boolean;
    };
    enabled_screening_services?: boolean;
  };
};

export type RecommendationMethod = "none" | "score_based" | "rule_based";

export type Template = {
  id: string;
  name: string;
  description: string;
  updated_at: string;
  updated_by?: {
    id: number;
    email: string;
    name: string;
  };
  created_at: string;
  created_by?: {
    id: number;
    email: string;
    name: string;
  };
  verifications: VerificationSchema[];
  verification_schemas: VerificationSchemas;
  applicant_fee: ApplicationFee;
  co_signer_fee: ApplicationFee;
  default?: boolean;
  costs?: CostsData;
  version?: number;
  applicant_paid?: boolean;
  co_signer_paid?: boolean;
  recommendation_method?: RecommendationMethod;
  screening_criteria?: ScreeningCriteriaType;
  rule_criteria?: RuleSet[];
  settings: IdentityVerificationSettings;
} & TemplateSettings;

export type CostItem = {
  name: string;
  description: string;
  amount: {
    cents: number;
    currency: string;
  };
  editable: boolean;
};

type CostsData = {
  [key in keyof ApplicantType]: CostItem[];
};

export type ApplicationFee = {
  cents: number;
  currency: string;
};

export type StepDetails = {
  groupKey?: string;
  key: string;
  type: string;
  title: string;
};

export type Question = {
  type: string;
  name: string;
  required: boolean;
};

export type ApplicantSettingsErrors = {
  gross_income?: string;
  gross_income_to_rent_ratio?: string;
  net_income?: string;
  debt_to_net_income?: string;
  sex_offender_check?: string;
  past_due_account?: string;
  credit_score?: string;
  unpaid_collections_balance?: string;
  bankruptcy_records?: string;
  housing_court_records?: string;
  collections_balance?: string;
};
